import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import useUpdateBankAccount from '@/features/dashboard/paymentConfig/hooks/useUpdateBankAccount.ts';
import {
  editBankAccountSchema,
  EditBankAccountFormData,
  EditBankAccountInputData,
} from '@/features/dashboard/paymentConfig/schemas/bankAccountSchema.ts';
import BankAccountForm from '@/features/dashboard/paymentConfig/components/BankAccountForm.tsx';
import { BankAccountDto } from '@/features/dashboard/paymentConfig/types/BankAccountDto.ts';
import { bankAccountMapper } from '@/features/dashboard/paymentConfig/types/BankAccountDto.mapper.ts';

interface EditBankAccountModalProps {
  bankAccount: BankAccountDto;
  onClose: () => void;
}

const EditBankAccountModal = ({
  bankAccount,
  onClose,
}: EditBankAccountModalProps) => {
  const updateMutation = useUpdateBankAccount(bankAccount.id);

  const methods = useForm<EditBankAccountInputData>({
    resolver: zodResolver(editBankAccountSchema),
    values: bankAccountMapper.fromDtoToEditForm(bankAccount),
  });

  const { handleSubmit } = methods;

  const onSubmit = (data: EditBankAccountFormData) => {
    updateMutation.mutate(data, { onSuccess: () => onClose() });
  };

  return (
    <Modal
      onClose={onClose}
      isDirty={methods.formState.isDirty}
      className="max-w-[620px]"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader title="Edytuj konto bankowe" onClose={onClose} />
          <ModalContent>
            <BankAccountForm isEdit />
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={updateMutation.isPending}
            >
              Zapisz zmiany
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditBankAccountModal;
